import {
  Group,
  Paper,
  SegmentedControl,
  Skeleton,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
} from "@mantine/core";
import RightSheetHeader from "./header/RightSheetHeader";
import { IconActivity, IconSpeakerphone } from "@tabler/icons-react";
import { useLeadsSheetStore } from "../../../../../../stores/LeadsStore";
import { useMemo } from "react";
import { useLanguage } from "../../../../../../stores/LanguageStore";
import TabSummary from "./summary/TabSummary";
import TabAnswers from "./answers/TabAnswers";
import TabNotes from "./notes/TabNotes";
import TabReminders from "./reminder/TabReminders";
import ProspectCampaigns from "./campaigns/ProspectCampaigns";
import NewReplyModal from "./answers/NewReplyModal";

const iconProps = { size: 16 };

const RightSheet = () => {
  const lang = useLanguage((s) => s.language);
  const loading = useLeadsSheetStore((s) => s.loading);
  const current = useLeadsSheetStore((s) => s.current);
  const tab = useLeadsSheetStore((s) => s.activeTab);
  const setTab = useLeadsSheetStore((s) => s.setActiveTab);

  const tabs = useMemo(() => {
    return [
      {
        label: (
          <Group justify="center">
            <IconActivity {...iconProps} />
            <span>{lang.prm.details.activity}</span>
          </Group>
        ),
        value: "activity",
        color: "primary",
      },
      {
        label: (
          <Group justify="center" wrap="nowrap">
            <IconSpeakerphone {...iconProps} />
            <span>{lang.prm.details.campaigns}</span>
            {current?.programmations?.length > 0 && (
              <ThemeIcon variant="light" radius={"sm"} size={"xs"} fz={"xs"}>
                {current?.programmations?.length}
              </ThemeIcon>
            )}
          </Group>
        ),
        value: "campaign",
        color: "tertiary.8",
      },
    ];
  }, [
    current?.programmations?.length,
    lang.prm.details.activity,
    lang.prm.details.campaigns,
  ]);

  return (
    <Stack
      w={"80%"}
      gap={0}
      h={"100%"}
      px={"lg"}
      pl={"xs"}
      py={"md"}
      style={{ borderTopRightRadius: 8, borderBottomRightRadius: 8 }}
      pos={"relative"}
    >
      {loading ? (
        <Stack h={"100%"}>
          <Skeleton height={75} />

          <Skeleton height={"100%"} />
        </Stack>
      ) : (
        <>
          <NewReplyModal />
          <RightSheetHeader />

          <Paper mt={24} component={Stack} gap={6} flex={1} px={"lg"}>
            <Group w={"100%"} wrap="nowrap">
              <SegmentedControl
                mt={"md"}
                value={tab === "campaign" ? "campaign" : "activity"}
                onChange={setTab}
                data={tabs}
                mb={"xs"}
                w={"50%"}
                mx={"auto"}
                size="md"
              />
            </Group>

            {tab !== "campaign" ? (
              <Tabs
                flex={1}
                value={tab === "activity" ? "summary" : tab}
                onChange={setTab}
                keepMounted={false}
              >
                <Tabs.List grow px={"lg"}>
                  <Tabs.Tab value="summary" fz={14}>
                    {lang.leads.tabs.summary}
                  </Tabs.Tab>
                  <Tabs.Tab value="answers" fz={14}>
                    {lang.leads.tabs.conversations}
                  </Tabs.Tab>
                  <Tabs.Tab value="notes" fz={14}>
                    {lang.leads.tabs.notes}
                  </Tabs.Tab>
                  <Tabs.Tab value="reminders" fz={14}>
                    {lang.leads.tabs.reminders}
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="summary" h={"calc(100% - 32px)"}>
                  <TabSummary />
                </Tabs.Panel>
                <Tabs.Panel value="answers" h={"calc(100% - 32px)"}>
                  <TabAnswers />
                </Tabs.Panel>
                <Tabs.Panel value="notes" h={"calc(100% - 32px)"}>
                  <TabNotes />
                </Tabs.Panel>
                <Tabs.Panel value="reminders" h={"calc(100% - 32px)"}>
                  <TabReminders />
                </Tabs.Panel>
              </Tabs>
            ) : (
              <ProspectCampaigns />
            )}
          </Paper>
        </>
      )}
    </Stack>
  );
};

export default RightSheet;
